import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from './AuthContext';

const SeasonContext = createContext();

export const useSeason = () => {
    const context = useContext(SeasonContext);
    if (!context) {
        throw new Error('useSeason must be used within a SeasonProvider');
    }
    return context;
};

export const SeasonProvider = ({ children }) => {
    const { user } = useAuth();
    const [currentSeason, setCurrentSeason] = useState(null);
    const [availableSeasons, setAvailableSeasons] = useState([]);
    const [adminOverride, setAdminOverride] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Get current season
    const fetchCurrentSeason = useCallback(async () => {
        try {
            const response = await api.getCurrentSeason();
            setCurrentSeason(response.data.currentSeason);
        } catch (err) {
            console.error('Error fetching current season:', err);
            setError('Failed to fetch current season');
        }
    }, []);

    // Get available seasons
    const fetchAvailableSeasons = useCallback(async () => {
        try {
            const response = await api.getAvailableSeasons();
            setAvailableSeasons(response.data.seasons);
        } catch (err) {
            console.error('Error fetching available seasons:', err);
            setError('Failed to fetch available seasons');
        }
    }, []);

    // Get admin override
    const fetchAdminOverride = useCallback(async () => {
        if (!user || user.role !== 'admin') {
            setAdminOverride(null);
            return;
        }

        try {
            const response = await api.getAdminSeasonOverride();
            setAdminOverride(response.data.overrideSeason);
        } catch (err) {
            console.error('Error fetching admin override:', err);
            setAdminOverride(null);
        }
    }, [user]);

    // Set admin override
    const setAdminSeasonOverride = async (season) => {
        if (!user || user.role !== 'admin') {
            throw new Error('Admin access required');
        }

        try {
            await api.setAdminSeasonOverride(season);
            setAdminOverride(season);
        } catch (err) {
            console.error('Error setting admin override:', err);
            throw err;
        }
    };

    // Clear admin override
    const clearAdminSeasonOverride = async () => {
        if (!user || user.role !== 'admin') {
            throw new Error('Admin access required');
        }

        try {
            await api.clearAdminSeasonOverride();
            setAdminOverride(null);
        } catch (err) {
            console.error('Error clearing admin override:', err);
            throw err;
        }
    };

    // Trigger season rollover
    const triggerSeasonRollover = async () => {
        if (!user || user.role !== 'admin') {
            throw new Error('Admin access required');
        }

        try {
            const response = await api.triggerAdminRollover();
            await fetchCurrentSeason();
            return response.data;
        } catch (err) {
            console.error('Error triggering season rollover:', err);
            throw err;
        }
    };

    // Get effective season (current season or admin override)
    const getEffectiveSeason = () => {
        return adminOverride || currentSeason;
    };

    // Initialize context
    useEffect(() => {
        const initializeSeason = async () => {
            setLoading(true);
            try {
                await Promise.all([
                    fetchCurrentSeason(),
                    fetchAvailableSeasons(),
                    fetchAdminOverride()
                ]);
            } catch (err) {
                console.error('Error initializing season context:', err);
                setError('Failed to initialize season context');
            } finally {
                setLoading(false);
            }
        };

        initializeSeason();
    }, [fetchCurrentSeason, fetchAvailableSeasons, fetchAdminOverride]);

    // Refresh admin override when user changes
    useEffect(() => {
        fetchAdminOverride();
    }, [fetchAdminOverride]);

    const value = {
        currentSeason,
        availableSeasons,
        adminOverride,
        effectiveSeason: getEffectiveSeason(),
        loading,
        error,
        setAdminSeasonOverride,
        clearAdminSeasonOverride,
        triggerSeasonRollover,
        refreshCurrentSeason: fetchCurrentSeason,
        refreshAvailableSeasons: fetchAvailableSeasons
    };

    return (
        <SeasonContext.Provider value={value}>
            {children}
        </SeasonContext.Provider>
    );
};
